import * as i2 from '@ngrx/store';
import { createAction, props, createFeatureSelector, createSelector, createReducer, on, StoreModule } from '@ngrx/store';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import { catchError, throwError, of } from 'rxjs';
import * as i1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { filter, withLatestFrom, switchMap, map, catchError as catchError$1 } from 'rxjs/operators';
import * as i3 from '@panamax/app-state';
import { CommonModule } from '@angular/common';
import * as i1$1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';

const CUSTOMER_REPORTING_ACTIONS = {
    resetReportState: createAction('[NgRx - Customer Reporting] Reset report filters and values'),
    // Mock actions
    loadCsvCustomerReport: createAction('[NgRx - Customer Reporting] Load csv customer report', props()),
    loadCsvCustomerReportSuccess: createAction('[NgRx - Customer Reporting] Load csv customer report success', props()),
    loadCsvCustomerReportFailed: createAction('[NgRx - Customer Reporting] Load csv customer report failed', props()),
    resetCsvCustomerReportError: createAction('[NgRx - Customer Reporting] Reset csv customer report error state'),
    loadJsonSpendByCategoryCustomerReport: createAction('[NgRx - Customer Reporting] Load json spend by category customer report', props()),
    loadJsonSpendByCategoryCustomerReportSuccess: createAction('[NgRx - Customer Reporting] Load spend by category json customer report success', props()),
    loadJsonSpendByCategoryCustomerReportFailed: createAction('[NgRx - Customer Reporting] Load spend by category json customer report failed', props()),
    loadJsonTopPurchasesCustomerReport: createAction('[NgRx - Customer Reporting] Load top purchases json customer report', props()),
    loadJsonTopPurchasesCustomerReportSuccess: createAction('[NgRx - Customer Reporting] Load top purchases json customer report success', props()),
    loadJsonTopPurchasesCustomerReportFailed: createAction('[NgRx - Customer Reporting] Load top purchases json customer report failed', props()),
};

const CUSTOMER_REPORTING_KEY = 'customerReporting';

const selectCustomerReport = createFeatureSelector(CUSTOMER_REPORTING_KEY);
const selectCsvCustomerReport = createSelector(selectCustomerReport, (state) => state.csvReport);
const selectSpendByCategoryCustomerReport = createSelector(selectCustomerReport, (state) => state.spendByCategoryReport);
const selectTopPurchasesCustomerReport = createSelector(selectCustomerReport, (state) => state.topPurchasesReport);
// LOADER SELECTORS
const selectIsCustomerReportingLoading = createSelector(selectCustomerReport, (state) => state.loading);
const selectIsSpendByCategoryLoading = createSelector(selectCustomerReport, (state) => state.loadingSpendByCategory);
const selectIsTopPurchasesLoading = createSelector(selectCustomerReport, (state) => state.loadingTopPurchases);
const selectIsDetailDataLoading = createSelector(selectCustomerReport, (state) => state.loadingDetailData);
const selectIsProductDetailLoading = createSelector(selectCustomerReport, (state) => state.loadingProductDetail);
const selectIsInvoiceHistoryLoading = createSelector(selectCustomerReport, (state) => state.loadingInvoiceHistory);
// ERROR SELECTORS
const selectCsvCustomerReportError = createSelector(selectCustomerReport, (state) => state.csvError);
const selectCustomerReportError = createSelector(selectCustomerReport, (state) => state.reportError);

const NGRX_CUSTOMER_REPORTING_LIB_CONFIG = new InjectionToken('NGRX_CUSTOMER_REPORTING_LIB_CONFIG');

class CustomerReportingDataService {
    constructor(config, http, store) {
        this.config = config;
        this.http = http;
        this.store = store;
        this.CSV_REPORT_ENDPOINT = 'reports/csv';
        this.JSON_REPORT_ENDPOINT = 'reports/json';
    }
    // TODO: update csv method to handle file, Blob?
    getCustomerReportCsv(callerInfo, reportName, reportFilters) {
        // TODO: Model the response? (they will be different for each report)
        const body = {
            callerInfo,
            reportInfo: {
                reportName,
                filters: reportFilters,
            },
        };
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        const apiUrl = `${this.config.apiUrl}${this.CSV_REPORT_ENDPOINT}`;
        return this.http
            .post(apiUrl, body, {
            headers: headers,
            responseType: 'blob',
            observe: 'response',
        })
            .pipe(catchError((error) => {
            this.store.dispatch(CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportFailed({
                report: reportName,
                error: 'API_ERROR',
                errorCode: error?.transactionId,
            }));
            return throwError(() => new Error('Failed to fetch customer report'));
        }));
    }
    getCustomerReportJson(callerInfo, reportName, reportFilters) {
        const body = {
            callerInfo,
            reportInfo: {
                reportName,
                filters: reportFilters,
            },
        };
        const apiUrl = `${this.config.apiUrl}${this.JSON_REPORT_ENDPOINT}`;
        return this.http
            .post(apiUrl, body)
            .pipe(catchError((error) => {
            this.store.dispatch(CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportFailed({
                report: reportName,
                error: 'API_ERROR',
                errorCode: error?.transactionId,
            }));
            return throwError(() => new Error('Failed to fetch customer report'));
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CustomerReportingDataService, deps: [{ token: NGRX_CUSTOMER_REPORTING_LIB_CONFIG }, { token: i1.HttpClient }, { token: i2.Store }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CustomerReportingDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CustomerReportingDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_CUSTOMER_REPORTING_LIB_CONFIG]
                }] }, { type: i1.HttpClient }, { type: i2.Store }]; } });

class CustomerReportingStateService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
    }
    initialize() {
        // Customer is needed to start making calls to API
        this.panAppState
            .getDistinctSelectedCustomer()
            .pipe(filter((customer) => customer?.selected))
            .subscribe((customer) => {
            this.store.dispatch(CUSTOMER_REPORTING_ACTIONS.resetReportState());
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CustomerReportingStateService, deps: [{ token: i2.Store }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CustomerReportingStateService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CustomerReportingStateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i2.Store }, { type: i3.PanAppState }]; } });

const initialReportState = {
    loading: false,
    csvReport: undefined,
    spendByCategoryReport: undefined,
    topPurchasesReport: undefined,
    csvError: null,
    reportError: null,
    // Loading State
    loadingSpendByCategory: false,
    loadingTopPurchases: false,
    loadingDetailData: false,
    loadingProductDetail: false,
    loadingInvoiceHistory: false,
};

// Stored procedure keys used for available reports
const REPORT_STORED_PROCEDURES = {
    INVOICE_HISTORY: 'SP_QUERY_INVOICE_HISTORY_DATA',
    PRODUCT_DETAIL: 'SP_QUERY_PRODUCT_DETAIL_DATA',
    SALES_DETAIL: 'SP_QUERY_SALES_DETAIL_DATA',
    SPEND_BY_CATEGORY: 'SP_QUERY_SPEND_BY_CATEGORY_DATA',
    TOP_PURCHASES: 'SP_QUERY_TOP_PURCHASES_DATA',
    CUSTOMER_REPORT: 'SP_EXEC_CUSTOMER_REPORT',
};

const customerReportsReducer = createReducer(initialReportState, on(CUSTOMER_REPORTING_ACTIONS.resetReportState, (state) => {
    return {
        ...state,
        ...initialReportState,
    };
}), on(CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReport, (state, { report }) => {
    if (report === 'SP_QUERY_PRODUCT_DETAIL_DATA') {
        return {
            ...state,
            loadingProductDetail: true,
            csvReport: report,
        };
    }
    else if (report === 'SP_QUERY_SALES_DETAIL_DATA') {
        return {
            ...state,
            loadingDetailData: true,
            csvReport: report,
        };
    }
    else if (report === 'SP_QUERY_INVOICE_HISTORY_DATA') {
        return {
            ...state,
            loadingInvoiceHistory: true,
            csvReport: report,
        };
    }
    return {
        ...state,
        loading: true,
        csvReport: report,
    };
}), on(CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportSuccess, (state, { report }) => {
    if (report === 'SP_QUERY_PRODUCT_DETAIL_DATA') {
        return {
            ...state,
            loadingProductDetail: false,
            csvReport: report,
        };
    }
    else if (report === 'SP_QUERY_SALES_DETAIL_DATA') {
        return {
            ...state,
            loadingDetailData: false,
            csvReport: report,
        };
    }
    else if (report === 'SP_QUERY_INVOICE_HISTORY_DATA') {
        return {
            ...state,
            loadingInvoiceHistory: false,
            csvReport: report,
        };
    }
    return {
        ...state,
        loading: false,
        csvReport: report,
    };
}), on(CUSTOMER_REPORTING_ACTIONS.loadCsvCustomerReportFailed, (state, { report, error, errorCode }) => {
    if (report === 'SP_QUERY_PRODUCT_DETAIL_DATA') {
        return {
            ...state,
            loadingProductDetail: false,
            csvError: {
                error,
                report,
                errorCode,
            },
        };
    }
    else if (report === 'SP_QUERY_SALES_DETAIL_DATA') {
        return {
            ...state,
            loadingDetailData: false,
            csvError: {
                error,
                report,
                errorCode,
            },
        };
    }
    else if (report === 'SP_QUERY_INVOICE_HISTORY_DATA') {
        return {
            ...state,
            loadingInvoiceHistory: false,
            csvError: {
                error,
                report,
                errorCode,
            },
        };
    }
    return {
        ...state,
        loading: false,
        csvError: {
            error,
            report,
            errorCode,
        },
    };
}), on(CUSTOMER_REPORTING_ACTIONS.resetCsvCustomerReportError, (state) => {
    return { ...state, csvError: null };
}), on(CUSTOMER_REPORTING_ACTIONS.loadJsonSpendByCategoryCustomerReport, (state) => {
    return {
        ...state,
        loadingSpendByCategory: true,
    };
}), on(CUSTOMER_REPORTING_ACTIONS.loadJsonSpendByCategoryCustomerReportSuccess, (state, { report }) => {
    return {
        ...state,
        loadingSpendByCategory: false,
        spendByCategoryReport: report,
    };
}), on(CUSTOMER_REPORTING_ACTIONS.loadJsonTopPurchasesCustomerReport, (state) => {
    return { ...state, loadingTopPurchases: true };
}), on(CUSTOMER_REPORTING_ACTIONS.loadJsonTopPurchasesCustomerReportSuccess, (state, { report }) => {
    return {
        ...state,
        loadingTopPurchases: false,
        topPurchasesReport: report,
    };
}), on(CUSTOMER_REPORTING_ACTIONS.loadJsonSpendByCategoryCustomerReportFailed, (state, { error }) => {
    return { ...state, loadingSpendByCategory: false, reportError: error };
}), on(CUSTOMER_REPORTING_ACTIONS.loadJsonTopPurchasesCustomerReportFailed, (state, { error }) => {
    return { ...state, loadingTopPurchases: false, reportError: error };
}));

class CustomerReportingEffects {
    constructor(actions$, customerReportingDataService, panAppState) {
        this.actions$ = actions$;
        this.customerReportingDataService = customerReportingDataService;
        this.panAppState = panAppState;
        this.getSpendByCategoryJsonReportEffect$ = createEffect(() => this.actions$.pipe(ofType(CUSTOMER_REPORTING_ACTIONS.loadJsonSpendByCategoryCustomerReport), withLatestFrom(this.panAppState.user$), switchMap(([{ storedProcedure, filters }, user]) => this.customerReportingDataService
            .getCustomerReportJson({
            userName: user.userId,
            firstName: user.firstName,
            lastName: user.lastName,
        }, storedProcedure, filters)
            .pipe(map((report) => {
            return CUSTOMER_REPORTING_ACTIONS.loadJsonSpendByCategoryCustomerReportSuccess({ report });
        }), catchError$1((error) => of(CUSTOMER_REPORTING_ACTIONS.loadJsonSpendByCategoryCustomerReportFailed({
            error: error.message ||
                'Something went wrong, please try again later',
        })))))));
        this.getTopPurchasesJsonReportEffect$ = createEffect(() => this.actions$.pipe(ofType(CUSTOMER_REPORTING_ACTIONS.loadJsonTopPurchasesCustomerReport), withLatestFrom(this.panAppState.user$), switchMap(([{ storedProcedure, filters }, user]) => this.customerReportingDataService
            .getCustomerReportJson({
            userName: user.userId,
            firstName: user.firstName,
            lastName: user.lastName,
        }, storedProcedure, filters)
            .pipe(map((report) => {
            return CUSTOMER_REPORTING_ACTIONS.loadJsonTopPurchasesCustomerReportSuccess({ report });
        }), catchError$1((error) => of(CUSTOMER_REPORTING_ACTIONS.loadJsonTopPurchasesCustomerReportFailed({
            error: error.message ||
                'Something went wrong, please try again later',
        })))))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CustomerReportingEffects, deps: [{ token: i1$1.Actions }, { token: CustomerReportingDataService }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CustomerReportingEffects }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CustomerReportingEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: CustomerReportingDataService }, { type: i3.PanAppState }]; } });

class NgrxCustomerReportingModule {
    constructor(ngRxCustomerReportingLibConfig, customerReportingState, store, panAppState) {
        this.ngRxCustomerReportingLibConfig = ngRxCustomerReportingLibConfig;
        this.customerReportingState = customerReportingState;
        this.store = store;
        this.panAppState = panAppState;
        this.subscripions = [];
    }
    static forRoot(ngRxCustomerReportingLibConfig, store, panAppState) {
        return {
            ngModule: NgrxCustomerReportingModule,
            providers: [
                {
                    provide: NGRX_CUSTOMER_REPORTING_LIB_CONFIG,
                    useValue: ngRxCustomerReportingLibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeCustomerReportingState,
                    deps: [CustomerReportingStateService],
                    multi: true,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgrxCustomerReportingModule, deps: [{ token: NGRX_CUSTOMER_REPORTING_LIB_CONFIG }, { token: CustomerReportingStateService }, { token: i2.Store }, { token: i3.PanAppState }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NgrxCustomerReportingModule, imports: [CommonModule, i2.StoreFeatureModule, i1$1.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgrxCustomerReportingModule, providers: [CustomerReportingStateService, CustomerReportingEffects], imports: [CommonModule,
            StoreModule.forFeature(CUSTOMER_REPORTING_KEY, customerReportsReducer),
            EffectsModule.forFeature([CustomerReportingEffects])] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NgrxCustomerReportingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        CommonModule,
                        StoreModule.forFeature(CUSTOMER_REPORTING_KEY, customerReportsReducer),
                        EffectsModule.forFeature([CustomerReportingEffects]),
                    ],
                    providers: [CustomerReportingStateService, CustomerReportingEffects],
                    exports: [],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_CUSTOMER_REPORTING_LIB_CONFIG]
                }] }, { type: CustomerReportingStateService }, { type: i2.Store }, { type: i3.PanAppState }]; } });
function initializeCustomerReportingState(customerReportingState) {
    const state = () => customerReportingState.initialize();
    return state;
}

/*
 * Public API Surface of ngrx-customer-reporting
 */
// actions

/**
 * Generated bundle index. Do not edit.
 */

export { CUSTOMER_REPORTING_ACTIONS, CustomerReportingDataService, CustomerReportingStateService, NgrxCustomerReportingModule, REPORT_STORED_PROCEDURES, initialReportState, selectCsvCustomerReport, selectCsvCustomerReportError, selectCustomerReportError, selectIsCustomerReportingLoading, selectIsDetailDataLoading, selectIsInvoiceHistoryLoading, selectIsProductDetailLoading, selectIsSpendByCategoryLoading, selectIsTopPurchasesLoading, selectSpendByCategoryCustomerReport, selectTopPurchasesCustomerReport };
